import { Link } from "react-router";
import type { PublicStory } from "@storyai/shared";
import { EmptyState, ErrorPanel, Loading, PageHeader } from "../components/ui";
import { usePublicStories } from "../lib/queries";

export function StoryCard({ story, children }: { story: PublicStory; children?: React.ReactNode }) {
  return (
    <Link to={`/stories/${story.id}`} className="story-card">
      {story.coverUrl ? (
        <img className="cover" src={story.coverUrl} alt="" loading="lazy" />
      ) : (
        <div className="cover cover-placeholder" aria-hidden="true">
          {story.title.slice(0, 1).toUpperCase()}
        </div>
      )}
      <div className="story-card-body">
        <div className="story-card-title">{story.title}</div>
        <div className="muted small">by {story.author.displayName}</div>
        {story.blurb && <p className="story-card-blurb muted">{story.blurb}</p>}
        <div className="story-card-meta">
          {story.rating && <span className="badge badge-muted">{story.rating}</span>}
          {story.versions.length > 0 && (
            <span className="badge badge-muted" title={story.versions.map((v) => v.title).join(", ")}>
              {story.versions.length} {story.versions.length === 1 ? "version" : "versions"}
            </span>
          )}
          {story.tags.map((t) => (
            <span key={t} className="tag">
              {t}
            </span>
          ))}
        </div>
        {children}
      </div>
    </Link>
  );
}

export function PublicStoriesPage() {
  const q = usePublicStories();

  return (
    <div className="page">
      <PageHeader title="Public Stories" subtitle="Published stories from everyone." />
      {q.isPending && <Loading />}
      {q.isError && <ErrorPanel error={q.error} />}
      {q.data &&
        (q.data.stories.length === 0 ? (
          <EmptyState title="No public stories yet">
            When authors publish, their stories show up here.{" "}
            <Link to="/my-stories">Write the first one.</Link>
          </EmptyState>
        ) : (
          <div className="grid-cards">
            {q.data.stories.map((s) => (
              <StoryCard key={s.id} story={s} />
            ))}
          </div>
        ))}
    </div>
  );
}
